import { useEffect, useRef, useState, type RefObject } from 'react';

interface FileDropOptions {
  /**
   * The region a drop counts for. Listening here rather than on the drop
   * surface itself keeps the target stable while the pointer crosses message
   * bubbles, avatars and the composer on its way in.
   */
  targetRef: RefObject<HTMLElement>;
  onFiles: (files: File[]) => void;
  /**
   * Turns the drop off without turning the guard off — a file dropped on a
   * page that ignores it still must not navigate the tab away from an unsent
   * draft.
   */
  enabled?: boolean;
}

/** Dragged text, a link or a card carries no files; only a real file drag does. */
function carriesFiles(event: DragEvent): boolean {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

/**
 * Drag files anywhere over `targetRef` to hand them to `onFiles`, and returns
 * whether a drag is currently over it so the caller can show where it will land.
 *
 * Listeners sit on the document because a drop that misses the target is the
 * dangerous one: the browser's default is to open the file as a page, which
 * throws away whatever was typed. Everything is caught here and only drops
 * inside the target do anything.
 */
export function useFileDrop({ targetRef, onFiles, enabled = true }: FileDropOptions): boolean {
  const [over, setOver] = useState(false);
  /* Read inside listeners that are attached once, so a re-render with a fresh
     closure does not have to re-bind them. */
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    const isInside = (event: DragEvent) =>
      enabledRef.current &&
      event.target instanceof Node &&
      Boolean(targetRef.current?.contains(event.target));

    const onDragOver = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      /* Both dragover and drop have to be prevented, or the browser opens the
         file instead of letting the page have it. */
      event.preventDefault();
      const inside = isInside(event);
      if (event.dataTransfer) event.dataTransfer.dropEffect = inside ? 'copy' : 'none';
      setOver(inside);
    };

    const onDrop = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      setOver(false);
      if (!isInside(event)) return;
      const files = Array.from(event.dataTransfer?.files ?? []);
      if (files.length) onFilesRef.current(files);
    };

    /* `relatedTarget` is null only when the pointer left the window itself;
       leaving one child for the next fires the same event and must not clear
       the highlight, which would flicker it across the whole region. */
    const onDragLeave = (event: DragEvent) => {
      if (!event.relatedTarget) setOver(false);
    };
    const onDragEnd = () => setOver(false);

    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', onDrop);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('dragend', onDragEnd);
    return () => {
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', onDrop);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('dragend', onDragEnd);
    };
  }, [targetRef]);

  useEffect(() => {
    if (!enabled) setOver(false);
  }, [enabled]);

  return over;
}
